<template>
    <section class="reset grid-center">
        <div class="reset-box">
            <router-link to="/">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-x-lg close-icon"
                    viewBox="0 0 16 16"
                >
                    <path
                        d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
                    />
                </svg>
            </router-link>
            <img src="@/assets/images/My Oxytocin Trademark.png" alt="My Oxytocin Trademark" class="trademark-logo" />
            
            <!-- Success State -->
            <div v-if="resetSuccess" class="success-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="#10b981" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                </svg>
                <h2>Password Reset Successfully!</h2>
                <p>Your password has been updated. You can now log in with your new password.</p>
                <router-link class="btn-login" to="/login">Go to Login</router-link>
            </div>
            
            <!-- Error State -->
            <div v-else-if="invalidLink" class="error-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="#ef4444" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                </svg>
                <h2>Invalid or Expired Link</h2>
                <p>This password reset link is invalid or has expired. Please request a new one.</p>
                <router-link class="btn-login" to="/forgot-password">Request New Link</router-link>
            </div>
            
            <!-- Reset Form -->
            <form v-else @submit.prevent="resetPassword">
                <h2>Create New Password</h2>
                <p class="email-info">for <strong>{{ email }}</strong></p>
                
                <div class="form-group">
                    <label>New Password</label>
                    <div class="password-input">
                        <input 
                            :type="showPassword ? 'text' : 'password'"
                            v-model="password"
                            required 
                            placeholder="Enter new password"
                            :disabled="loading"
                            @input="checkPasswordStrength"
                        />
                        <svg
                            @click="showPassword = !showPassword"
                            v-if="showPassword"
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            class="eye-icon"
                            viewBox="0 0 16 16"
                        >
                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                        </svg>
                        <svg
                            @click="showPassword = !showPassword"
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            class="eye-icon"
                            viewBox="0 0 16 16"
                        >
                            <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                            <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                            <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                        </svg>
                    </div>
                    
                    <!-- Password Strength Indicator -->
                    <div class="password-strength" v-if="password">
                        <div class="strength-bar">
                            <div class="strength-fill" :class="strengthClass" :style="{ width: strengthPercent + '%' }"></div>
                        </div>
                        <span class="strength-text" :class="strengthClass">{{ strengthText }}</span>
                    </div>
                </div>
                
                <div class="form-group">
                    <label>Confirm Password</label>
                    <div class="password-input">
                        <input 
                            :type="showConfirmPassword ? 'text' : 'password'"
                            v-model="confirmPassword"
                            required 
                            placeholder="Confirm new password"
                            :disabled="loading"
                        />
                        <svg
                            @click="showConfirmPassword = !showConfirmPassword"
                            v-if="showConfirmPassword"
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            class="eye-icon"
                            viewBox="0 0 16 16"
                        >
                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                        </svg>
                        <svg
                            @click="showConfirmPassword = !showConfirmPassword"
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            class="eye-icon"
                            viewBox="0 0 16 16"
                        >
                            <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
                            <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
                            <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
                        </svg>
                    </div>
                    <p class="match-error" v-if="confirmPassword && password !== confirmPassword">
                        Passwords do not match
                    </p>
                </div>

                <button type="submit" class="btn-reset" :disabled="loading || !isFormValid">
                    <span v-if="loading" class="spinner"></span>
                    <span v-else>Reset Password</span>
                </button>
            </form>
        </div>
    </section>
</template>

<script>
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from "@/firebase/init.js";

export default {
    name: "ResetPassword",
    data() {
        return {
            password: "",
            confirmPassword: "",
            email: "",
            oobCode: "",
            loading: false,
            showPassword: false,
            showConfirmPassword: false,
            resetSuccess: false,
            invalidLink: false,
            passwordStrength: 0,
        };
    },
    computed: {
        isFormValid() {
            return this.password.length >= 6 && 
                   this.password === this.confirmPassword &&
                   this.passwordStrength >= 2;
        },
        strengthClass() {
            if (this.passwordStrength <= 1) return 'weak';
            if (this.passwordStrength === 2) return 'fair';
            if (this.passwordStrength === 3) return 'good';
            return 'strong';
        },
        strengthText() {
            if (this.passwordStrength <= 1) return 'Weak';
            if (this.passwordStrength === 2) return 'Fair';
            if (this.passwordStrength === 3) return 'Good';
            return 'Strong';
        },
        strengthPercent() {
            return (this.passwordStrength / 4) * 100;
        }
    },
    async mounted() {
        // Get the oobCode from URL
        this.oobCode = this.$route.query.oobCode;
        
        if (!this.oobCode) {
            this.invalidLink = true;
            return;
        }
        
        try {
            // Verify the password reset code and get the email
            this.email = await verifyPasswordResetCode(auth, this.oobCode);
        } catch (error) {
            console.error('Invalid reset code:', error);
            this.invalidLink = true;
        }
    },
    methods: {
        checkPasswordStrength() {
            let strength = 0;
            const password = this.password;
            
            if (password.length >= 6) strength++;
            if (password.length >= 10) strength++;
            if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength++;
            if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) strength++;
            
            this.passwordStrength = strength;
        },
        async resetPassword() {
            if (!this.isFormValid) return;
            
            this.loading = true;
            
            try {
                await confirmPasswordReset(auth, this.oobCode, this.password);
                this.resetSuccess = true;
            } catch (error) {
                console.error('Password reset error:', error);
                
                let errorMessage = 'Failed to reset password. Please try again.';
                
                switch (error.code) {
                    case 'auth/expired-action-code':
                        errorMessage = 'This reset link has expired. Please request a new one.';
                        break;
                    case 'auth/invalid-action-code':
                        errorMessage = 'This reset link is invalid. Please request a new one.';
                        break;
                    case 'auth/weak-password':
                        errorMessage = 'Password is too weak. Please use at least 6 characters.';
                        break;
                }
                
                this.$swal({
                    icon: 'error',
                    title: 'Reset Failed',
                    text: errorMessage,
                    confirmButtonColor: '#e37267',
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.grid-center {
    display: grid;
    place-items: center;
}

.reset {
    background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
    min-height: 100vh;
    padding: 20px;
}

.reset-box {
    text-align: center;
    background-color: white;
    border-radius: 20px;
    padding: 50px 40px;
    min-width: 320px;
    max-width: 420px;
    width: 100%;
    margin-inline: auto;
    position: relative;
    box-shadow: 0 10px 40px rgba(227, 114, 103, 0.1);
}

.close-icon {
    position: absolute;
    right: 20px;
    top: 20px;
    cursor: pointer;
    transition: color 0.2s;
    color: #999;
}

.close-icon:hover {
    color: #e37267;
}

.trademark-logo {
    width: 180px;
    margin-bottom: 20px;
}

.reset-box h2 {
    font-weight: 600;
    color: #333;
    margin: 0 0 10px;
    font-size: 24px;
}

.email-info {
    color: #666;
    font-size: 14px;
    margin-bottom: 25px;
}

.email-info strong {
    color: #333;
}

.reset-box form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group {
    text-align: left;
}

.form-group label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    margin-bottom: 8px;
}

.password-input {
    position: relative;
}

.password-input input {
    width: 100%;
    padding: 14px 45px 14px 16px;
    border: 2px solid #e6d1c7;
    border-radius: 12px;
    font-size: 15px;
    transition: all 0.3s;
    background: #fafafa;
}

.password-input input:focus {
    outline: none;
    border-color: #e37267;
    background: white;
    box-shadow: 0 0 0 4px rgba(227, 114, 103, 0.1);
}

.password-input input:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
}

.eye-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #999;
    transition: color 0.2s;
}

.eye-icon:hover {
    color: #e37267;
}

/* Password Strength */
.password-strength {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.strength-bar {
    flex: 1;
    height: 6px;
    background: #eee;
    border-radius: 3px;
    overflow: hidden;
}

.strength-fill {
    height: 100%;
    border-radius: 3px;
    transition: all 0.3s;
}

.strength-fill.weak { background: #ef4444; }
.strength-fill.fair { background: #f59e0b; }
.strength-fill.good { background: #10b981; }
.strength-fill.strong { background: #059669; }

.strength-text {
    font-size: 12px;
    font-weight: 600;
    min-width: 50px;
}

.strength-text.weak { color: #ef4444; }
.strength-text.fair { color: #f59e0b; }
.strength-text.good { color: #10b981; }
.strength-text.strong { color: #059669; }

.match-error {
    color: #ef4444;
    font-size: 12px;
    margin-top: 6px;
}

.btn-reset {
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, #e37267 0%, #d4605a 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    margin-top: 10px;
}

.btn-reset:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(227, 114, 103, 0.4);
}

.btn-reset:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.spinner {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Success & Error States */
.success-message,
.error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 30px 0;
}

.success-message h2 {
    color: #10b981;
}

.error-message h2 {
    color: #ef4444;
}

.success-message p,
.error-message p {
    color: #666;
    font-size: 15px;
    line-height: 1.6;
    max-width: 280px;
}

.btn-login {
    display: inline-block;
    margin-top: 10px;
    padding: 14px 40px;
    background: linear-gradient(135deg, #e37267 0%, #d4605a 100%);
    color: white;
    text-decoration: none;
    border-radius: 25px;
    font-weight: 600;
    font-size: 15px;
    transition: all 0.3s;
}

.btn-login:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(227, 114, 103, 0.4);
}

@media (max-width: 480px) {
    .reset-box {
        padding: 40px 25px;
    }
}
</style>
